import React, { useState, useEffect, useCallback } from 'react';
import { 
  Star, 
  CheckCircle, 
  XCircle, 
  Trash2, 
  Edit, 
  Search, 
  Filter, 
  Sparkles, 
  Clock, 
  MessageSquare, 
  RefreshCw,
  Eye,
  Check,
  X,
  Pin
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../config/api';

export default function FeedbackManagement() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    averageRating: '5.0'
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'pending', 'approved', 'rejected'
  const [searchQuery, setSearchQuery] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all'); // 'all', '5', '4', '3', '2', '1'
  
  // Edit modal state
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    role: '',
    rating: 5,
    comment: '',
    status: 'approved',
    featured: false
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch feedback from admin endpoint
  const fetchFeedbacks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/feedback/admin');
      if (res.ok) {
        const data = await res.json();
        setFeedbacks(data.feedbacks || []);
        if (data.stats) {
          setStats(data.stats);
        }
      } else {
        toast.error('Failed to fetch feedbacks');
      }
    } catch (err) {
      console.error('Error fetching admin feedbacks:', err);
      toast.error('Error loading feedback list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  // Handle Approve
  const handleApprove = async (id) => {
    try {
      const res = await api.put(`/feedback/${id}`, {
        status: 'approved',
        isApproved: true
      });
      if (res.ok) {
        toast.success('Feedback approved and published!');
        fetchFeedbacks();
      } else {
        toast.error('Failed to approve feedback');
      }
    } catch (err) {
      console.error('Error approving feedback:', err);
      toast.error('Error approving feedback');
    }
  };

  // Handle Reject
  const handleReject = async (id) => {
    try {
      const res = await api.put(`/feedback/${id}`, {
        status: 'rejected',
        isApproved: false
      });
      if (res.ok) {
        toast.success('Feedback marked as rejected');
        fetchFeedbacks();
      } else {
        toast.error('Failed to reject feedback');
      }
    } catch (err) {
      console.error('Error rejecting feedback:', err);
      toast.error('Error rejecting feedback');
    }
  };

  // Handle Feature / Pin Toggle
  const handleToggleFeatured = async (feedback) => {
    try {
      const newFeatured = !feedback.featured;
      const res = await api.put(`/feedback/${feedback.id}`, {
        featured: newFeatured
      });
      if (res.ok) {
        toast.success(newFeatured ? 'Feedback featured!' : 'Feedback unfeatured');
        fetchFeedbacks();
      } else {
        toast.error('Failed to update featured status');
      }
    } catch (err) {
      console.error('Error updating featured status:', err);
      toast.error('Error updating featured status');
    }
  };

  // Handle Delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this feedback?')) {
      return;
    }

    try {
      const res = await api.delete(`/feedback/${id}`);
      if (res.ok) {
        toast.success('Feedback deleted');
        fetchFeedbacks();
      } else {
        toast.error('Failed to delete feedback');
      }
    } catch (err) {
      console.error('Error deleting feedback:', err);
      toast.error('Error deleting feedback');
    }
  };

  // Open Edit Modal
  const handleStartEdit = (item) => {
    setEditingFeedback(item);
    setEditFormData({
      name: item.name || '',
      role: item.role || '',
      rating: item.rating || 5,
      comment: item.comment || '',
      status: item.status || (item.isApproved ? 'approved' : 'pending'),
      featured: !!item.featured
    });
  };

  // Save Edit Changes
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingFeedback) return;

    try {
      setIsUpdating(true);
      const res = await api.put(`/feedback/${editingFeedback.id}`, {
        name: editFormData.name,
        role: editFormData.role,
        rating: Number(editFormData.rating),
        comment: editFormData.comment,
        status: editFormData.status,
        isApproved: editFormData.status === 'approved',
        featured: editFormData.featured
      });

      if (res.ok) {
        toast.success('Feedback updated successfully!');
        setEditingFeedback(null);
        fetchFeedbacks();
      } else {
        toast.error('Failed to update feedback');
      }
    } catch (err) {
      console.error('Error updating feedback:', err);
      toast.error('Error saving feedback');
    } finally {
      setIsUpdating(false);
    }
  };

  // Filtered feedbacks based on tab, search, rating
  const filteredFeedbacks = feedbacks.filter((item) => {
    // Tab filter
    if (activeTab === 'pending' && item.status !== 'pending' && item.isApproved) return false;
    if (activeTab === 'approved' && item.status !== 'approved' && !item.isApproved) return false;
    if (activeTab === 'rejected' && item.status !== 'rejected') return false;

    // Rating filter
    if (ratingFilter !== 'all' && String(item.rating) !== ratingFilter) return false;

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = (item.name || '').toLowerCase().includes(q);
      const matchComment = (item.comment || '').toLowerCase().includes(q);
      const matchRole = (item.role || '').toLowerCase().includes(q);
      const matchEmail = (item.email || '').toLowerCase().includes(q);
      if (!matchName && !matchComment && !matchRole && !matchEmail) return false;
    }

    return true;
  });

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-red-600 rounded-3xl p-6 sm:p-8 text-white shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-amber-200 text-xs sm:text-sm font-semibold uppercase tracking-wider mb-1">
            <Sparkles className="w-4 h-4" />
            <span>Moderation & Reviews</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Star className="w-8 h-8 text-amber-300 fill-amber-300" />
            Feedback & Reviews Management
          </h2>
          <p className="text-orange-100 text-sm sm:text-base mt-1 max-w-xl">
            Review community ratings, approve testimonials for the homepage 3-second slideshow, and feature top feedback.
          </p>
        </div>

        <button
          onClick={fetchFeedbacks}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold text-sm transition-all hover:scale-105"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Summary Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Total Reviews */}
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-5 border border-orange-200/60 shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-orange-700 uppercase tracking-wider">Total Reviews</span>
            <MessageSquare className="w-5 h-5 text-orange-500" />
          </div>
          <div className="text-3xl font-extrabold text-gray-900">{stats.total}</div>
        </div>

        {/* Average Rating */}
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-5 border border-amber-200/60 shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Average Rating</span>
            <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
          </div>
          <div className="text-3xl font-extrabold text-amber-600 flex items-center gap-1.5">
            {stats.averageRating}
            <span className="text-sm font-normal text-gray-500">/ 5.0</span>
          </div>
        </div>

        {/* Pending Approval */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-200/60 shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">Pending Review</span>
            <Clock className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-3xl font-extrabold text-blue-600 flex items-center gap-2">
            {stats.pendingCount}
            {stats.pendingCount > 0 && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 animate-pulse">
                Needs Action
              </span>
            )}
          </div>
        </div>

        {/* Approved & Live */}
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-5 border border-emerald-200/60 shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Live on Homepage</span>
            <CheckCircle className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="text-3xl font-extrabold text-emerald-600">{stats.approvedCount}</div>
        </div>
      </div>

      {/* Filter Tabs & Search Bar */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 sm:p-5 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        {/* Tabs */}
        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-2 md:pb-0">
          {[
            { id: 'all', label: 'All Reviews', count: stats.total },
            { id: 'pending', label: 'Pending Approval', count: stats.pendingCount },
            { id: 'approved', label: 'Approved', count: stats.approvedCount },
            { id: 'rejected', label: 'Rejected', count: stats.rejectedCount }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold whitespace-nowrap transition-all flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              <span>{tab.label}</span>
              <span
                className={`text-[11px] px-1.5 py-0.2 rounded-full ${
                  activeTab === tab.id
                    ? 'bg-white/30 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Search & Star Rating Filter */}
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* Star Filter */}
          <div className="relative">
            <select
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value)}
              className="px-3 py-2 text-xs sm:text-sm font-semibold border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Stars ⭐</option>
              <option value="5">5 Stars ⭐⭐⭐⭐⭐</option>
              <option value="4">4 Stars ⭐⭐⭐⭐</option>
              <option value="3">3 Stars ⭐⭐⭐</option>
              <option value="2">2 Stars ⭐⭐</option>
              <option value="1">1 Star ⭐</option>
            </select>
          </div>

          {/* Search Bar */}
          <div className="relative flex-1 sm:w-64">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search comments or name..."
              className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Feedback Items List */}
      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Loading reviews...</p>
        </div>
      ) : filteredFeedbacks.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-md">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-gray-800 mb-1">No Reviews Found</h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            {searchQuery || ratingFilter !== 'all' || activeTab !== 'all'
              ? 'No feedback matches the selected filters.'
              : 'No feedback submissions yet.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {filteredFeedbacks.map((item) => {
            const isApproved = item.status === 'approved' || item.isApproved;
            const isPending = item.status === 'pending' || (!item.isApproved && item.status !== 'rejected');
            const isRejected = item.status === 'rejected';

            return (
              <div
                key={item.id}
                className={`bg-white rounded-3xl p-5 sm:p-6 shadow-md border transition-all duration-300 flex flex-col justify-between ${
                  item.featured
                    ? 'border-amber-400 ring-2 ring-amber-300/40'
                    : isPending
                    ? 'border-blue-200 bg-blue-50/20'
                    : 'border-gray-100 hover:border-orange-200'
                }`}
              >
                <div>
                  {/* Top bar with rating, badges, and status */}
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${
                            star <= item.rating
                              ? 'text-amber-400 fill-amber-400'
                              : 'text-gray-200'
                          }`}
                        />
                      ))}
                      <span className="text-xs font-bold text-gray-700 ml-1">
                        {item.rating}★
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      {/* Featured Badge */}
                      {item.featured && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                          <Pin className="w-3 h-3 text-amber-600" /> Featured
                        </span>
                      )}

                      {/* Status Badge */}
                      {isApproved && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                          <CheckCircle className="w-3.5 h-3.5" /> Approved
                        </span>
                      )}
                      {isPending && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800 animate-pulse">
                          <Clock className="w-3.5 h-3.5" /> Pending Review
                        </span>
                      )}
                      {isRejected && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800">
                          <XCircle className="w-3.5 h-3.5" /> Rejected
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Comment */}
                  <p className="text-gray-800 text-sm sm:text-base leading-relaxed mb-4 italic font-normal">
                    "{item.comment}"
                  </p>
                </div>

                {/* Author Info & Date */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-3 text-xs">
                    <div>
                      <div className="font-bold text-gray-900 text-sm">{item.name || 'Anonymous'}</div>
                      <div className="text-orange-600 font-medium">{item.role || 'Community Member'}</div>
                      {item.email && (
                        <div className="text-gray-400 text-[11px]">{item.email}</div>
                      )}
                    </div>
                    <div className="text-right text-gray-400 text-[11px]">
                      {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : 'Recent'}
                    </div>
                  </div>

                  {/* Action Buttons Bar */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                    <div className="flex items-center gap-1.5">
                      {!isApproved && (
                        <button
                          onClick={() => handleApprove(item.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-sm transition-all"
                          title="Approve for live homepage"
                        >
                          <Check className="w-3.5 h-3.5" />
                          Approve
                        </button>
                      )}
                      {!isRejected && (
                        <button
                          onClick={() => handleReject(item.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-red-100 text-gray-700 hover:text-red-700 font-semibold text-xs transition-all"
                          title="Reject feedback"
                        >
                          <X className="w-3.5 h-3.5" />
                          Reject
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleFeatured(item)}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg font-semibold text-xs transition-all ${
                          item.featured
                            ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'
                        }`}
                        title={item.featured ? 'Unfeature from top' : 'Feature / Pin on top'}
                      >
                        <Pin className="w-3.5 h-3.5" />
                        {item.featured ? 'Featured' : 'Pin'}
                      </button>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleStartEdit(item)}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                        title="Edit Review"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete Review"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Feedback Modal */}
      {editingFeedback && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-gray-100 animate-scaleUp">
            <div className="flex items-center justify-between pb-4 mb-4 border-b">
              <h3 className="text-xl font-bold text-gray-900">Edit Feedback</h3>
              <button
                onClick={() => setEditingFeedback(null)}
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Author Name</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full px-3.5 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Role / Subtitle</label>
                <input
                  type="text"
                  value={editFormData.role}
                  onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                  className="w-full px-3.5 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Rating (1-5)</label>
                  <select
                    value={editFormData.rating}
                    onChange={(e) => setEditFormData({ ...editFormData, rating: Number(e.target.value) })}
                    className="w-full px-3.5 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-orange-500"
                  >
                    {[5, 4, 3, 2, 1].map((r) => (
                      <option key={r} value={r}>{r} Stars</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Status</label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                    className="w-full px-3.5 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Comment</label>
                <textarea
                  rows={4}
                  value={editFormData.comment}
                  onChange={(e) => setEditFormData({ ...editFormData, comment: e.target.value })}
                  className="w-full px-3.5 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-orange-500 resize-none"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="featCheck"
                  checked={editFormData.featured}
                  onChange={(e) => setEditFormData({ ...editFormData, featured: e.target.checked })}
                  className="rounded border-gray-300 text-orange-500 focus:ring-orange-500 w-4 h-4"
                />
                <label htmlFor="featCheck" className="ml-2 text-xs font-medium text-gray-700 cursor-pointer">
                  Feature / Pin on top of homepage slideshow
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setEditingFeedback(null)}
                  className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdating}
                  className="px-6 py-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm shadow hover:from-orange-600 hover:to-amber-600"
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
